/**
 * What an output's menu and the pane heads do to an output (stage 2b):
 * archive it (at once, with the five second Undo, which restores it through
 * the API), restore it, and export a document in the version it is at.
 */
import { useQueryClient } from "@tanstack/react-query";
import { Archive } from "lucide-react";
import { getAiRequestErrorMessage } from "@/lib/api";
import { queue } from "@/components/Toast";
import { useWorkshopArtifactMutations, workshopArtifactQuery } from "../api/useWorkshopArtifacts";
import { useWorkshopIdeaMutations } from "../api/useWorkshopIdeas";
import type { WorkshopOutputKind } from "../workshopOutputs";
import type { ExportFormat } from "./WorkshopOutputMenu";
import {
  exportArtifactDocx,
  exportArtifactMarkdown,
  exportArtifactPdf,
} from "./exportWorkshopArtifact";
import { useWorkshopUndo } from "./WorkshopUndo";

type Target = { kind: WorkshopOutputKind; id: string; title: string };

export function useOutputActions(projectId: string, brandId: string) {
  const qc = useQueryClient();
  const undo = useWorkshopUndo();
  const { archiveArtifact } = useWorkshopArtifactMutations(projectId);
  const { archiveIdea } = useWorkshopIdeaMutations(projectId, brandId);

  const send = (target: Target, archived: boolean) => {
    if (target.kind === "document") {
      return archiveArtifact.mutateAsync({ artifactId: target.id, archived });
    }
    if (target.kind === "idea") {
      return archiveIdea.mutateAsync({ ideaId: target.id, archived });
    }
    return Promise.reject(new Error("Pictures are not archived."));
  };

  const failed = (title: string) => (error: unknown) =>
    queue.add({ title, description: getAiRequestErrorMessage(error), variant: "error" });

  const restore = (target: Target) => {
    send(target, false).catch(failed(`Couldn't restore “${target.title}”`));
  };

  /** Archive happens at once; Undo restores it through the API. */
  const archive = (target: Target) => {
    const key = undo.show({
      icon: Archive,
      title: `“${target.title}” archived`,
      line: "It leaves Outputs. Show archived brings it back.",
      undo: () => restore(target),
    });
    // A refused archive takes its Undo down with it: there is nothing to undo.
    send(target, true).catch((error) => {
      undo.dismiss(key);
      failed(`Couldn't archive “${target.title}”`)(error);
    });
  };

  /** A document, in its current version, as a file. */
  const exportDocument = async (documentId: string, format: ExportFormat) => {
    try {
      const doc = await qc.fetchQuery({ ...workshopArtifactQuery(documentId), staleTime: 30_000 });
      if (format === "md") exportArtifactMarkdown(doc.title, doc.content, doc.version);
      else if (format === "pdf") await exportArtifactPdf(doc.title, doc.content, doc.version);
      else await exportArtifactDocx(doc.title, doc.content, doc.version);
      queue.add({
        title: `Exported v${doc.version}`,
        description: "The document stays editable in Workshop.",
        variant: "success",
      });
    } catch (error) {
      failed("Couldn't export the document")(error);
    }
  };

  return { archive, restore, exportDocument };
}
